import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HttpClient, HttpEvent, HttpEventType, HttpResponse, HttpParams } from '@angular/common/http';
import { filter, map, tap } from 'rxjs/operators';
import { pipe } from 'rxjs';
import { AlertService, IAlertType } from "../../_shared/services";
import { NgIf } from "@angular/common";
import { ClrInputModule, ClrSelectModule } from "@clr/angular";
import { FileUploadComponent } from "./file-upload/file-upload.component";

import { uploadBaseUrl } from "../../_shared/utils/rest-url";

interface FileUploadResponse {
  status: string;
  data?: { id: string; file_name?: string };
}

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [
    NgIf,
    ClrInputModule,
    ClrSelectModule,
    FileUploadComponent,
    ReactiveFormsModule
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.sass'
})
export class UploadComponent implements OnInit, OnChanges {
  public uploadFile: FormGroup;
  success = false;
  progress = 0;

  @Input() companyId: number = 0;
  @Input() componentId: string = '';
  @Input() projectId: string = '';
  @Input() technicalDrawing: string = '';
  @Input() materialId: number = 0;
  @Input() jobId: number = 0;
  @Input() subId: number = 0;
  @Input() docType: number = 0;
  @Output() closeModalEvent = new EventEmitter();
  @Output() uploadedFile = new EventEmitter<{ id: string; fileName?: string }>();

  private filesdirectory = uploadBaseUrl();

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public alertDispatcher: AlertService
  ) {
    this.uploadFile = this.fb.group({
      company_id: [this.companyId],
      doctype: [this.docType, Validators.required],
      project: [this.projectId],
      job_number: [this.jobId],
      sub_number: [this.subId],
      technicalDrawing: [this.technicalDrawing],
      material_id: [this.materialId],
      description: ['', Validators.required],
      component: [this.componentId],
      file: [null, [Validators.required]]
    });
  }

  ngOnInit() {
    this.syncFormFromInputs();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.syncFormFromInputs();
  }

  closeModal() {
    this.closeModalEvent.emit(false);
  }

  submit() {
    this.success = false;
    if (!this.uploadFile.valid) {
      markAllAsDirty(this.uploadFile);

      // Show error message for invalid file type
      const fileControl = this.uploadFile.get('file');
      if (fileControl?.errors?.['invalidFileType']) {
        this.alertDispatcher.show({
          message: fileControl.errors['invalidFileType'],
          clrAlertType: IAlertType.DANGER
        }, 2000);
      }

      return;
    }

    const formValue = this.uploadFile.value;
    const fileValue = this.uploadFile.get('file')?.value;
    let selectedFile: File | null = null;
    if (fileValue instanceof File) {
      selectedFile = fileValue;
    } else if (fileValue && typeof fileValue === 'object' && 'length' in fileValue) {
      selectedFile = fileValue.item ? fileValue.item(0) : fileValue[0];
    }
    if (selectedFile) {
      console.debug('[upload] selected file', {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type
      });
    }
    if (!selectedFile || selectedFile.size === 0) {
      this.alertDispatcher.show({
        message: 'Bitte eine gültige Datei auswählen.',
        clrAlertType: IAlertType.DANGER
      }, 2000);
      return;
    }
    let params = new HttpParams()
      .set('description', formValue.description)
      .set('doctype', String(formValue.doctype || this.docType || 0))
      .set('job_number', String(this.jobId))
      .set('project', String(this.projectId || formValue.project || '0'))
      .set('sub_number', String(this.subId));

    if (Number(this.companyId || formValue.company_id) > 0) {
      params = params.set('company_id', String(this.companyId || formValue.company_id));
    }

    if (this.componentId || formValue.component) {
      params = params.set('component', String(this.componentId || formValue.component));
    }
    if (this.technicalDrawing || formValue.technicalDrawing) {
      params = params.set('technical_drawing', String(this.technicalDrawing || formValue.technicalDrawing));
    }
    if (Number(this.materialId || formValue.material_id) > 0) {
      params = params.set('material_id', String(this.materialId || formValue.material_id));
    }

    // Create FormData only for the file
    const formData = new FormData();
    formData.append('file', selectedFile);

    this.http.post<FileUploadResponse>(`${this.filesdirectory}/upload`, formData, {
      params: params,
      reportProgress: true,
      observe: 'events'
    }).pipe(
      uploadProgress(progress => (this.progress = progress)),
      toResponseBody<FileUploadResponse>()
    ).subscribe({
      next: res => {
        const uploadedId = res?.data?.id;
        if (uploadedId) {
          this.uploadedFile.emit({ id: uploadedId, fileName: res?.data?.file_name });
        }
        this.progress = 0;
        this.success = true;

        this.alertDispatcher.show({
          message: ' Datei erfolgreich hochgeladen. ',
          clrAlertType: IAlertType.INFO
        }, 2000);

        this.uploadFile.reset();
        this.closeModal();
      },
      error: (error) => {
        this.alertDispatcher.show({
          message: ' Upload fehlgeschlagen: ' + error.message,
          clrAlertType: IAlertType.DANGER
        }, 2000);
        return;
      }
    });
  }

  hasError(field: string, error: string) {
    const control = this.uploadFile.get(field);
    return control?.dirty && control?.hasError(error);
  }

  private syncFormFromInputs(): void {
    this.uploadFile.patchValue({
      company_id: this.companyId,
      doctype: this.docType,
      project: this.projectId,
      job_number: this.jobId,
      sub_number: this.subId,
      technicalDrawing: this.technicalDrawing,
      material_id: this.materialId,
      component: this.componentId
    });
  }
}

export function markAllAsDirty(form: FormGroup) {
  for (const control of Object.keys(form.controls)) {
    form.controls[control].markAsDirty();
  }
}

export function uploadProgress<T>(cb: (progress: number) => void) {
  return tap((event: HttpEvent<T>) => {
    if (event.type === HttpEventType.UploadProgress) {
      cb(Math.round((100 * event.loaded) / (event.total ?? 1)));
    }
  });
}

export function toResponseBody<T>() {
  return pipe(
    filter((event: HttpEvent<T>): event is HttpResponse<T> => event.type === HttpEventType.Response),
    map((res) => res.body)
  );
}
